import type { Board } from "./[boardId].page";
import { theme } from "antd";
import { useRouter } from "next/router";
import {
  PureComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useEffectOnce } from "usehooks-ts";
import {
  DashboardV1State,
  DeepLinkType,
  ExploreCard,
  SnippetV1State,
} from "pages/api/deep-links/types";
import { useSnippets } from "src/hooks/useSnippets";
import { stringifyEqual } from "src/utils/objects";
import { Condition, SearchState } from "src/search/types";
import { asKey, fromKey } from "src/utils/fields";
import {
  serializeParams,
  unserializeParams,
} from "src/search/lib/params-state-reducer";
import { QuestionTitlebar } from "./QuestionTitlebar";
import { QuestionViewer } from "./QuestionViewer";
import { mergeState } from "./lib";

export function QuestionInit(props: {
  board: Board;
  colorTheme?: string;
  json: Pick<DashboardV1State, "data">;
  cardId: string;
  onArrange(): void;
}) {
  const card = props.json.data[props.cardId];
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");

  const { token } = theme.useToken();
  const router = useRouter();

  // ...will be used as a prop for a PureCompoment
  const serializedCard = JSON.stringify(card);

  /***************************************
   *  - `cardState`
   *  This is the fundamental query state we are
   *  working with. Normally this is in the payload of
   *  the card, with an exception for pivots.
   *
   *  A card may have pivot tags, in which case it will
   *  look up snippets that match the tag and offer them
   *  as "pivots" to the user. The title becomes a dropdown
   *  that they can change.
   *
   *  In order for this to work smoothly, the list of pivots
   *  _MUST BE_ ready when the card is loaded. No promising.
   *
   *  If the query string contains `${cardId}-pivotId`, then
   *  they query used in the snippet identified by that ID
   *  becomes the `cardState` we use.
   **/
  const pivots = useSnippets({
    isForCard: true,
    isTagged: card.snippetTags,
  }).list;

  const pivotId = pivots.some(
    (pivot) => pivot.id === router.query[`${props.cardId}-pivotId`]
  )
    ? router.query[`${props.cardId}-pivotId`]
    : undefined;

  const setPivotId = useCallback(
    (pivotId = "") => {
      const params = { ...router.query };
      const pivotKey = `${props.cardId}-pivotId`;
      const oldPivotKey = `${params[pivotKey] || ""}`;
      if (oldPivotKey === pivotKey) {
        return;
      }
      if (pivotId) params[pivotKey] = pivotId;
      else delete params[pivotKey];
      // also reset the state, it may no longer make sense...
      const stateKey = `${props.cardId}-state`;
      delete params[stateKey];
      router.replace({ pathname: router.pathname, query: params });
    },
    [props.cardId, router]
  );

  const cardState = useMemo(() => {
    const pivot = pivots.find((pivot) => pivot.id === pivotId);
    const params = (pivot?.json as SnippetV1State | undefined)?.params;
    return params || card.state.params;
  }, [card, pivots, pivotId, props.cardId]);

  /***************************
   * - `globalState`
   *   The global state is limited to date ranges and filters.
   *   These values take precendence in any conflicts
   *   with the `cardState`.
   *
   *   The `globalState` is represented in the board's globalState,
   *   with the global names, ie: Activity.Actor
   *
   *   But when a card is working with global state, it will
   *   be using any mappings the card may have to build
   *   a global state, ie: mapping Activity.Actor to Commentor
   */
  const globalDuration = props.board.globalState?.duration;
  const globalStartDate = props.board.globalState?.startDate;
  const globalEndDate = props.board.globalState?.endDate;
  const globalFilters: Condition[] = useMemo(() => {
    try {
      return props.board.globalState?.filters || [];
    } catch {
      return [];
    }
  }, [props.board.globalState?.filters]);

  const mappedGlobalFilters = useMemo(() => {
    if (!globalFilters) return [];
    if (!card.mappings || card.mappings.length === 0) return globalFilters;
    return globalFilters.map((filter) => {
      const unMappedKey = asKey(filter);
      const mapping = card.mappings?.find(
        (m) => m.fromFieldKey === unMappedKey
      );
      if (!mapping) return filter;
      return { ...filter, ...fromKey(mapping.toFieldKey) };
    });
  }, [globalFilters, card.mappings]);

  const globalState: SearchState = useMemo(() => {
    return globalDuration
      ? ({
          duration: globalDuration,
          filters: mappedGlobalFilters,
          orgId: props.board.globalState?.orgId,
        } as SearchState)
      : globalStartDate && globalEndDate
      ? ({
          startDate: globalStartDate,
          endDate: globalEndDate,
          filters: mappedGlobalFilters,
          orgId: props.board.globalState?.orgId,
        } as SearchState)
      : ({
          filters: mappedGlobalFilters,
          orgId: props.board.globalState?.orgId,
        } as SearchState);
  }, [
    mappedGlobalFilters,
    globalDuration,
    globalStartDate,
    globalEndDate,
    props.cardId,
    props.board.globalState?.orgId,
  ]);

  /*****************************
   * - `resetState`
   * Card + Global, allows the card to be reset,
   * and also for smartly determining the modified state.
   */
  const resetState = useMemo(() => {
    const {
      duration: _0,
      startDate: _1,
      endDate: _2,
      filters: _3,
      ...baseState
    } = cardState;
    const resetState: SearchState = baseState;

    if (globalState.duration) {
      resetState.duration = globalState.duration;
      delete resetState.startDate;
      delete resetState.endDate;
    } else if (globalState.startDate) {
      resetState.startDate = globalState.startDate;
      resetState.endDate = globalState.endDate;
      delete resetState.duration;
    } // we disregard the date range info on the card itself
    // this disregard doesn't come without a cost as it
    // breaks the interactivity of the timeseries graph (VZBL-1420)

    // quick lookup to prevent dupes
    const filtersSet = new Set<string>();
    resetState.filters = [];
    globalState.filters?.forEach((filter) => {
      const key = asKey(filter);
      if (!filtersSet.has(key)) {
        filtersSet.add(key);
        resetState.filters!.push(filter);
      }
    });
    // we don't disregard the card's filters, though.
    cardState.filters?.forEach((filter) => {
      const key = asKey(filter);
      if (!filtersSet.has(key)) {
        filtersSet.add(key);
        resetState.filters!.push(filter);
      }
    });

    return resetState;
  }, [cardState, globalState]);

  /*****************************
   * - `modifiedState`
   *   Any changes done at the card level.
   *   For example, applying a snippet, or exlcuding nulls. etc.
   *
   *   After some cleanup, we can put the `modifiedState` into
   *   the query string with the name `${cardId}-state`.
   *
   *   The cleanup is effectively a diff to the
   *   merged `{card,global}State`, wrt to mappings.
   *
   *   - If something new appears in the changeState,
   *     then it is added to the modified state
   *   - If something no longer appears in the changeState,
   *     then it is added to the modified state
   */
  const modifiedState: SearchState = useMemo(() => {
    try {
      const blob = router.query[`${props.cardId}-state`] || "{}";
      return JSON.parse(`${blob}`) as SearchState;
    } catch {
      return {} as SearchState;
    }
  }, [router.query, props.cardId]);

  const setModifiedState = useCallback(
    (changeState: SearchState) => {
      const nextModifiedState = {} as SearchState;
      // check each part, one by one, and add to destiny
      // only if it is needed to recreate the state.
      if (
        !stringifyEqual(
          changeState.aggregations || [],
          resetState.aggregations || []
        )
      )
        nextModifiedState.aggregations = changeState.aggregations;
      if (
        !stringifyEqual(
          changeState.compareWith || {},
          resetState.compareWith || {}
        )
      )
        nextModifiedState.compareWith = changeState.compareWith;
      if (
        !stringifyEqual(changeState.duration || "", resetState.duration || "")
      )
        nextModifiedState.duration = changeState.duration;
      if (!stringifyEqual(changeState.endDate || "", resetState.endDate || ""))
        nextModifiedState.endDate = changeState.endDate;
      nextModifiedState.endDate = changeState.endDate;
      if (!stringifyEqual(changeState.filters || [], resetState.filters || []))
        nextModifiedState.filters = changeState.filters;
      if (
        !stringifyEqual(changeState.formulas || [], resetState.formulas || [])
      )
        nextModifiedState.formulas = changeState.formulas;
      if (
        !stringifyEqual(changeState.groupBys || [], resetState.groupBys || [])
      )
        nextModifiedState.groupBys = changeState.groupBys;
      if (!stringifyEqual(changeState.having || [], resetState.having || []))
        nextModifiedState.having = changeState.having;
      if (!stringifyEqual(changeState.search || "", resetState.search || ""))
        nextModifiedState.search = changeState.search;
      if (!stringifyEqual(changeState.select || [], resetState.select || []))
        nextModifiedState.select = changeState.select;
      if (
        !stringifyEqual(changeState.startDate || "", resetState.startDate || "")
      )
        nextModifiedState.startDate = changeState.startDate;

      // If anything was added, let's set it.
      // If nothing was added, we may need to clear it
      if (Object.keys(nextModifiedState).length > 0) {
        router.replace({
          pathname: router.pathname,
          query: {
            ...router.query,
            [`${props.cardId}-state`]: JSON.stringify(nextModifiedState),
          },
        });
      }
    },
    [resetState, router, props.cardId]
  );

  /*****************************
   * - `appliedState`
   *   We tactfully merge the three states above into something
   *   that can be serialized and passed as a prop to a PureComponent
   *
   *   Modified has precendence, then global, then card.
   *   Date ranges follow the normal duration OR start/end
   *   Fitlers replace by name
   */
  const appliedState = useMemo(() => {
    const {
      duration: _0,
      startDate: _1,
      endDate: _2,
      filters: _3,
      ...baseState
    } = cardState;
    const appliedState: SearchState = baseState;

    // TODO this impl came before reset state, and
    //      should be using that as a dependency
    if (modifiedState.duration) {
      appliedState.duration = modifiedState.duration;
      delete appliedState.startDate;
      delete appliedState.endDate;
    } else if (modifiedState.startDate) {
      appliedState.startDate = modifiedState.startDate;
      appliedState.endDate = modifiedState.endDate;
      delete appliedState.duration;
    } else if (globalState.duration) {
      appliedState.duration = globalState.duration;
      delete appliedState.startDate;
      delete appliedState.endDate;
    } else if (globalState.startDate) {
      appliedState.startDate = globalState.startDate;
      appliedState.endDate = globalState.endDate;
      delete appliedState.duration;
    } // we disregard the date range info on the card itself

    if (
      Object.keys(modifiedState).includes("aggregations") &&
      !stringifyEqual(
        modifiedState.aggregations || [],
        appliedState.aggregations || []
      )
    )
      appliedState.aggregations = modifiedState.aggregations;
    if (
      Object.keys(modifiedState).includes("compareWith") &&
      !stringifyEqual(
        modifiedState.compareWith || {},
        appliedState.compareWith || {}
      )
    )
      appliedState.compareWith = modifiedState.compareWith;
    if (
      Object.keys(modifiedState).includes("formula") &&
      !stringifyEqual(modifiedState.formulas || [], appliedState.formulas || [])
    )
      appliedState.formulas = modifiedState.formulas;
    if (
      Object.keys(modifiedState).includes("groupBys") &&
      !stringifyEqual(modifiedState.groupBys || [], appliedState.groupBys || [])
    )
      appliedState.groupBys = modifiedState.groupBys;
    if (
      Object.keys(modifiedState).includes("having") &&
      !stringifyEqual(modifiedState.having || [], appliedState.having || [])
    )
      appliedState.having = modifiedState.having;
    if (
      Object.keys(modifiedState).includes("search") &&
      !stringifyEqual(modifiedState.search || "", appliedState.search || "")
    )
      appliedState.search = modifiedState.search;
    if (
      Object.keys(modifiedState).includes("select") &&
      !stringifyEqual(modifiedState.select || [], appliedState.select || [])
    )
      appliedState.select = modifiedState.select;

    // quick lookup to prevent dupes
    const filtersSet = new Set<string>();
    appliedState.filters = [];
    modifiedState.filters?.forEach((filter) => {
      const key = asKey(filter);
      filtersSet.add(key);
      appliedState.filters!.push(filter);
    });
    globalState.filters?.forEach((filter) => {
      const key = asKey(filter);
      if (!filtersSet.has(key)) {
        filtersSet.add(key);
        appliedState.filters!.push(filter);
      }
    });
    // we don't disregard the card's filters, though.
    cardState.filters?.forEach((filter) => {
      const key = asKey(filter);
      if (!filtersSet.has(key)) {
        filtersSet.add(key);
        appliedState.filters!.push(filter);
      }
    });

    return appliedState;
  }, [cardState, globalState, modifiedState, props.cardId]);

  // we need the applied state for this one, that's why it isn't
  // up by the other `modifiedState` code
  // this one is obnoxious, it needs to _never_ change so that it
  // can be passed deep into the card without causing rerenders
  const mergeRef = useRef({ appliedState, setModifiedState });
  mergeRef.current = { appliedState, setModifiedState };
  const mergeModifiedState = useCallback(
    (state: SearchState) =>
      mergeRef.current.setModifiedState(
        mergeState(mergeRef.current.appliedState, state)
      ),
    [] // <--- Don't change this! dependencies to in ref above
  );

  const serializedState = useMemo(() => {
    return serializeParams(appliedState);
  }, [appliedState]);

  return (
    <div
      style={{
        height: "100%",
        position: "relative",
        backgroundColor: card.transparent
          ? "transparent"
          : props.colorTheme === "alternate"
          ? token.colorBgContainer
          : token.colorBgLayout,
        borderRadius: 10,
        border: "1px solid var(--color-border-secondary)",
      }}
    >
      <QuestionTitlebar
        {...props}
        searchState={appliedState}
        changed={Object.keys(modifiedState).length > 0}
        card={card}
        pivotId={`${pivotId || ""}`}
        setPivotId={setPivotId}
        setSearchState={setModifiedState}
        onReset={() => setModifiedState(resetState)}
      />
      <QuestionRenderer
        cardId={props.cardId}
        pivotId={typeof pivotId === "string" ? pivotId : ""}
        serializedCard={serializedCard}
        serializedState={serializedState}
        mergeModifiedState={mergeModifiedState}
      />
    </div>
  );
}

class QuestionRenderer extends PureComponent<{
  cardId: string;
  pivotId: string;
  serializedCard: string;
  serializedState: string;
  mergeModifiedState(state: SearchState): void;
}> {
  render() {
    const card: ExploreCard = JSON.parse(this.props.serializedCard);
    const state: SearchState = unserializeParams(this.props.serializedState);
    if (card.type !== DeepLinkType.EXPLORE)
      throw new Error("expected question card");

    return (
      <ReloadingQuestionViewer
        card={card}
        cardId={this.props.cardId}
        state={state}
        pivoting={Boolean(this.props.pivotId)}
        mergeModifiedState={this.props.mergeModifiedState}
      />
    );
  }
}

function ReloadingQuestionViewer(props: {
  card: ExploreCard;
  cardId: string;
  state: SearchState;
  pivoting: boolean;
  mergeModifiedState(state: SearchState): void;
}) {
  const [changeNumber, setChangeNumber] = useState(0);

  useEffect(() => {
    setChangeNumber((n) => n + 1);
  }, [props]);

  const [checkpoint, setCheckpoint] = useState(0);

  if (checkpoint !== changeNumber)
    return <ReloadTrigger onLoad={() => setCheckpoint(changeNumber)} />;
  return <QuestionViewer {...props} />;
}

function ReloadTrigger(props: { onLoad(): void }) {
  useEffectOnce(() => {
    props.onLoad();
  });
  return null;
}
